import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {WalkCard} from 'UI/components/modules/WalkCard';

import type {Walk} from 'types/payload-types';

/**
 * Props for the WalkTeaserBlock component.
 */
interface ComponentProps {
    /** Optional label for a link to the walk's detail page below the card. */
    actionLabel?: string | null;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'WalkTeaserBlock'
     */
    testId?: string;
    /** The populated Walk document to tease. */
    walk?: Walk | string | null;
}

/**
 * Renders a single walk as a teaser card, reusing `WalkCard`. Used to highlight one specific
 * walk within a Landingpage or the Homepage, as opposed to `FeaturedWalksBlock`'s curated list.
 *
 * @param props             The component props.
 * @param props.actionLabel Optional label for a link below the card.
 * @param props.testId      An optional test ID. Defaults to `'WalkTeaserBlock'`.
 * @param props.walk        The populated Walk document to display.
 * @returns A JSX element rendering the walk teaser, or `null` if the walk isn't populated.
 *
 * @example
 * ```tsx
 * <WalkTeaserBlock walk={walk} actionLabel="Jetzt entdecken" />
 * ```
 */
const WalkTeaserBlock = ({actionLabel, testId = 'WalkTeaserBlock', walk}: ComponentProps) => {
    if (!walk || typeof walk !== 'object') return null;

    return (
        <Wrapper data-cy={testId}>
            <WalkCard walk={walk} />
            {actionLabel && (
                <ActionLink data-cy={`${testId}-action`} href={`/walks/${walk.slug}`}>
                    {actionLabel}
                </ActionLink>
            )}
        </Wrapper>
    );
};

WalkTeaserBlock.displayName = 'WalkTeaserBlock';

export {WalkTeaserBlock};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
    max-width: 48rem;
`;

const ActionLink = styled(Link)`
    align-items: center;
    color: ${({theme}) => theme.colors.primaryDefault};
    display: inline-flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    font-weight: 700;
    min-height: 4.8rem;
    text-decoration: underline;
`;